import React, { useEffect, useState } from "react";
import {
  Table,
  Modal,
  Button,
  Badge,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import { MdOutlineSort } from "react-icons/md";
import { FiInfo, FiX, FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import "bootstrap/dist/css/bootstrap.min.css";

const TabellaRimessiCompleta = () => {
  const [rimessiRaggruppati, setRimessiRaggruppati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "Data Rimesso",
    direction: "asc",
  });
  const [showModal, setShowModal] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [selectedRimesso, setSelectedRimesso] = useState(null);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((data) => {
        const datiRaw = data?.RIMESSI?.flat() || [];
        const raggruppati = datiRaw.reduce((acc, curr) => {
          const numDoc = curr["Num Rimesso"];
          if (!acc[numDoc]) {
            acc[numDoc] = {
              ...curr,
              dettaglioArticoli: [curr],
              totQta: parseFloat(curr.Qta || 0),
            };
          } else {
            acc[numDoc].dettaglioArticoli.push(curr);
            acc[numDoc].totQta += parseFloat(curr.Qta || 0);
          }
          return acc;
        }, {});
        const ordinati = Object.values(raggruppati).sort((a, b) => {
          return new Date(a["Data Rimesso"]) - new Date(b["Data Rimesso"]);
        });

        setRimessiRaggruppati(ordinati);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore fetch:", err);
        setLoading(false);
      });
  }, []);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });

    const sortedData = [...rimessiRaggruppati].sort((a, b) => {
      let valA = a[key] || "";
      let valB = b[key] || "";
      if (key === "Data Rimesso") {
        valA = new Date(valA);
        valB = new Date(valB);
      }
      return direction === "asc"
        ? valA > valB
          ? 1
          : -1
        : valA < valB
          ? 1
          : -1;
    });
    setRimessiRaggruppati(sortedData);
  };

  const renderTooltipSpiegazione = (props) => (
    <Tooltip
      id="info-rimessi-tooltip"
      {...props}
      style={{ ...props.style, fontSize: "0.85rem" }}
    >
      Questo pannello mostra la <strong>Gestione dei Rimessi</strong> (merce
      respinta o reinviata al fornitore). I dati sono raggruppati per numero di
      documento. Puoi <strong>ordinare la tabella</strong> cliccando sulle
      intestazioni delle colonne e <strong>cliccare su una riga</strong> per
      esaminare i singoli codici articolo coinvolti.
    </Tooltip>
  );

  const isOlderThan5Days = (dataRimesso) => {
    if (!dataRimesso) return false;
    const oggi = new Date();
    const data = new Date(dataRimesso);
    const diffTime = oggi - data;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays > 5;
  };

    const getStatusStyle = (dateStr) => {
    if (!dateStr) return { borderLeft: "4px solid #dee2e6" };
    const dataRimesso = new Date(dateStr.split(" ")[0]);
    const oggi = new Date();
    const diffDays = Math.floor((oggi - dataRimesso) / (1000 * 60 * 60 * 24));

    if (diffDays >= 15)
      return { borderLeft: "4px solid #dc3545", backgroundColor: "#fff5f5" };
    if (diffDays >= 7)
      return { borderLeft: "4px solid #fd7e14", backgroundColor: "#fffaf5" };
    if (diffDays >= 3)
      return { borderLeft: "4px solid #ffc107", backgroundColor: "#fffdf5" };

    return { borderLeft: "4px solid #198754" };
  };

  // Funzione helper per renderizzare le righe della tabella (condivisa tra widget e fullscreen)
  const renderTableRows = (isFullscreen = false) => {
    if (loading) {
      return (
        <tr>
          <td colSpan={isFullscreen ? "6" : "5"} className="text-center py-4 text-muted">
            Caricamento in corso...
          </td>
        </tr>
      );
    }

    if (rimessiRaggruppati.length === 0) {
      return (
        <tr>
          <td colSpan={isFullscreen ? "6" : "5"} className="text-center py-4 text-muted">
            Nessun rimesso trovato
          </td>
        </tr>
      );
    }

    return rimessiRaggruppati.map((row, idx) => {
      const urgente = isOlderThan5Days(row["Data Rimesso"]);
      return (
        <tr
          key={idx}
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedRimesso(row);
            setShowModal(true);
          }}
        >
          <td style={{ ...getStatusStyle(row["Data Rimesso"]), fontWeight: "700", verticalAlign: "middle" }}>{row["Dep"]}</td>
          <td
            style={{ fontWeight: "600", verticalAlign: "middle" }}
            className="text-dark text-truncate"
            title={row["Ragione Sociale"]}
          >
            {row["Ragione Sociale"]}
          </td>
          <td style={{ verticalAlign: "middle" }}>
            {row["Data Rimesso"]
              ? new Date(row["Data Rimesso"]).toLocaleDateString("it-IT")
              : "-"}
          </td>
          <td style={{ fontWeight: "700", verticalAlign: "middle" }} className="text-primary">
            {row["Num Rimesso"]}
          </td>
          {isFullscreen && (
            <td style={{ verticalAlign: "middle" }}>
              {urgente ? (
                <Badge bg="danger-subtle" className="text-danger border border-danger-subtle px-2 py-1" style={{ fontSize: "0.65rem" }}>
                  <FiAlertCircle className="me-1" /> URGENTE (&gt;5gg)
                </Badge>
              ) : (
                <Badge bg="success-subtle" className="text-success border border-success-subtle px-2 py-1" style={{ fontSize: "0.65rem" }}>
                  <FiCheckCircle className="me-1" /> RECENTE
                </Badge>
              )}
            </td>
          )}
          <td className="text-center style={{ verticalAlign: 'middle' }}">
            <Badge bg="light" text="dark" className="border px-2 py-1">
              {row.dettaglioArticoli.length} {isFullscreen ? "Articoli" : ""}
            </Badge>
          </td>
        </tr>
      );
    });
  };

  return (
    <div
      className="shadow-sm card border-0 p-0"
      style={{
        borderRadius: "16px",
        height: "380px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* Header statico del Widget */}
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
        style={{ zIndex: 110 }}
      >
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span
            style={{
              fontWeight: "800",
              fontSize: "0.75rem",
              color: "#1a1a1a",
              letterSpacing: "1px",
              marginRight: "8px",
            }}
          >
            GESTIONE RIMESSI
          </span>
          <Badge bg="secondary" className="me-2" style={{ fontSize: "0.55rem", padding: "4px 6px" }}>ESPANDI</Badge>

          <OverlayTrigger
            placement="bottom"
            delay={{ show: 200, hide: 200 }}
            overlay={renderTooltipSpiegazione}
          >
            <span
              onClick={(e) => e.stopPropagation()}
              style={{
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <FiInfo size={14} color="#adb5bd" />
            </span>
          </OverlayTrigger>
        </div>
        <small className="text-muted fw-bold">
          {rimessiRaggruppati.length} DOCUMENTI
        </small>
      </div>

      <style>{`
          .scroll-area::-webkit-scrollbar { width: 4px; }
          .scroll-area::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 10px; }
          .table th { cursor: pointer; user-select: none; background-color: #fff; }
          .table th:hover { background-color: #f8f9fa; }
          .table td { padding: 12px 8px !important; }
      `}</style>

      {/* Tabella standard all'interno della dashboard */}
      <div
        className="scroll-area p-0"
        style={{
          overflowY: "auto",
          flexGrow: 1,
        }}
      >
        <Table className="table table-hover mb-0" style={{ fontSize: "0.70rem" }}>
          <thead className="bg-white sticky-top" style={{ zIndex: 1, boxShadow: "0 2px 2px -1px rgba(0,0,0,0.05)" }}>
            <tr>
              <th onClick={() => requestSort("Dep")} style={{ width: "70px" }}>Dep</th>
              <th onClick={() => requestSort("Ragione Sociale")}>Fornitore <MdOutlineSort /></th>
              <th onClick={() => requestSort("Data Rimesso")} style={{ width: "110px" }}>Data</th>
              <th onClick={() => requestSort("Num Rimesso")} style={{ width: "130px" }}>Num. Rimesso</th>
              <th className="text-center" style={{ width: "80px" }}>Articoli</th>
            </tr>
          </thead>
          <tbody>
            {renderTableRows(false)}
          </tbody>
        </Table>
      </div>

      {/* MODALE TUTTO SCHERMO PER LA COMPLETA GESTIONE */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                GESTIONE RIMESSI - VISTA ESTANSA
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltipSpiegazione}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <Badge bg="dark" className="px-3 py-2" style={{ fontSize: "0.75rem" }}>
                {rimessiRaggruppati.length} TOTALI
              </Badge>
              <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
                <FiX size={16} /> Chiudi
              </Button>
            </div>
          </div>
          
          <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
            <div className="bg-white shadow-sm rounded-4 overflow-hidden border">
              <Table responsive className="table table-hover mb-0 align-middle" style={{ fontSize: "0.85rem" }}>
                <thead className="table-light border-bottom">
                  <tr>
                    <th onClick={() => requestSort("Dep")} className="p-3" style={{ width: "90px" }}>Dep</th>
                    <th onClick={() => requestSort("Ragione Sociale")} className="p-3">Fornitore <MdOutlineSort /></th>
                    <th onClick={() => requestSort("Data Rimesso")} className="p-3" style={{ width: "140px" }}>Data Rimesso</th>
                    <th onClick={() => requestSort("Num Rimesso")} className="p-3" style={{ width: "180px" }}>Numero Rimesso</th>
                    <th onClick={() => requestSort("Data Rimesso")} className="p-3" style={{ width: "160px" }}>Stato Invecchiamento</th>
                    <th className="text-center p-3" style={{ width: "140px" }}>Righe Articolo</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTableRows(true)}
                </tbody>
              </Table>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* MODALE DETTAGLIO ARTICOLI (SUB-LIVELLO) */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        scrollable
        dialogClassName="custom-modal-center"
      >
        <Modal.Header closeButton className="bg-light py-2 px-3 align-items-center">
          <Modal.Title style={{ fontSize: "0.85rem", letterSpacing: "0.5px" }} className="fw-bold text-secondary text-uppercase">
            Dettaglio Rimesso: <span className="text-primary">{selectedRimesso?.["Num Rimesso"]}</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0">
          <div className="p-3 bg-light border-bottom">
            <div className="row g-2">
              <div className="col-8">
                <span className="text-muted d-block" style={{ fontSize: "0.65rem", fontWeight: "700", textTransform: "uppercase" }}>
                  Ragione Sociale
                </span>
                <span className="fw-bold text-dark d-block text-truncate" style={{ fontSize: "0.85rem" }} title={selectedRimesso?.["Ragione Sociale"]}>
                  {selectedRimesso?.["Ragione Sociale"] || "---"}
                </span>
              </div>
              <div className="col-4 text-end">
                <span className="text-muted d-block" style={{ fontSize: "0.65rem", fontWeight: "700", textTransform: "uppercase" }}>
                  Totale Pezzi
                </span>
                <span className="badge bg-primary fs-6 mt-1">
                  {selectedRimesso?.totQta || 0}
                </span>
              </div>
            </div>
          </div>

          <Table responsive hover className="mb-0 align-middle">
            <thead className="table-light" style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <tr>
                <th className="ps-3 py-2 text-muted">Codice Articolo</th>
                <th className="text-center py-2 text-muted" style={{ width: "100px" }}>Qta Ord.</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "0.75rem" }}>
              {selectedRimesso?.dettaglioArticoli.map((art, i) => (
                <tr key={i}>
                  <td className="ps-3 py-2 fw-semibold text-secondary">
                    {art.articolo || "---"}
                  </td>
                  <td className="text-center py-2 fw-bold text-dark">
                    {Math.round(art.Qta)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>

        <Modal.Footer className="p-2 bg-light border-top">
          <Button variant="secondary" size="sm" className="px-3" style={{ fontSize: "0.75rem" }} onClick={() => setShowModal(false)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TabellaRimessiCompleta;